import React, { useEffect, useState, useRef } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
interface DashboardLayoutProps {
  sidebar: React.ReactNode;
  topBar: React.ReactNode;
  children: React.ReactNode;
  activeSection?: string;
}
const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  sidebar,
  topBar,
  children,
  activeSection
}) => {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [showAttentionArrows, setShowAttentionArrows] = useState(true);
  const contentAreaRef = useRef<HTMLDivElement>(null);
  const prevActiveSectionRef = useRef<string | undefined>(activeSection);
  // Scroll to top whenever activeSection changes
  useEffect(() => {
    if (activeSection && activeSection !== prevActiveSectionRef.current) {
      if (contentAreaRef.current) {
        contentAreaRef.current.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
      prevActiveSectionRef.current = activeSection;
      // Close mobile sidebar after navigation
      if (isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false);
      }
      // Show scroll indicator on mobile only
      if (window.innerWidth < 768) {
        setShowScrollIndicator(true);
        // Hide after 2 seconds
        setTimeout(() => {
          setShowScrollIndicator(false);
        }, 2000);
      }
    }
  }, [activeSection, isMobileSidebarOpen]);
  // Attention arrows cycle: show for 3.5s, hide, repeat every 10s
  useEffect(() => {
    const cycleArrows = () => {
      setShowAttentionArrows(true);
      setTimeout(() => {
        setShowAttentionArrows(false);
      }, 3500);
    };
    // Show immediately on mount
    cycleArrows();
    // Repeat every 10 seconds
    const interval = setInterval(cycleArrows, 10000);
    return () => clearInterval(interval);
  }, []);
  return <>
      <style data-id="element-11289">
        {`
          @keyframes pastelCycleWithPause {
            0% {
              color: #F5D0FF;
            }
            20% {
              color: #C8E6FF;
            }
            40% {
              color: #D7FFD9;
            }
            60% {
              color: #FFEACC;
            }
            60%, 100% {
              color: #FFEACC;
            }
          }

          @keyframes bigSparkleBurst {
            0% {
              transform: scale(0.5) rotate(0deg);
              opacity: 0;
            }
            10% {
              transform: scale(1.6) rotate(20deg);
              opacity: 1;
            }
            25% {
              transform: scale(1.2) rotate(-20deg);
              opacity: 1;
            }
            35% {
              transform: scale(0.8) rotate(0deg);
              opacity: 0;
            }
            35%, 100% {
              transform: scale(0.8) rotate(0deg);
              opacity: 0;
            }
          }

          @keyframes scrollBounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(5px);
            }
          }

          @keyframes scrollFadeIn {
            0% {
              opacity: 0;
              transform: translateY(-10px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes scrollFadeOut {
            0% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }

          @keyframes attentionArrowBounce {
            0%, 100% {
              transform: translateY(0);
              opacity: 1;
            }
            50% {
              transform: translateY(6px);
              opacity: 1;
            }
          }

          @keyframes attentionArrowFadeIn {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }

          @keyframes attentionArrowFadeOut {
            0% {
              opacity: 1;
            }
            100% {
              opacity: 0;
            }
          }

          .attention-arrow {
            animation: attentionArrowFadeIn 0.3s ease-out forwards, attentionArrowBounce 0.8s ease-in-out infinite;
          }

          .attention-arrow.fade-out {
            animation: attentionArrowFadeOut 0.4s ease-out forwards;
          }

          .scroll-indicator {
            animation: scrollFadeIn 0.4s ease-out forwards;
          }

          .scroll-indicator.fade-out {
            animation: scrollFadeOut 0.5s ease-out forwards;
          }

          .scroll-indicator-arrow {
            animation: scrollBounce 0.8s ease-in-out infinite;
          }

          @media (max-width: 767px) {
            .mobile-menu-icon {
              animation: pastelCycleWithPause 5s ease-in-out infinite;
            }

            .mobile-menu-button-wrapper {
              position: relative;
            }

            .mobile-menu-button-wrapper::before,
            .mobile-menu-button-wrapper::after,
            .mobile-menu-button-wrapper .sparkle-extra {
              content: "";
              position: absolute;
              width: 12px;
              height: 12px;
              background: #F3B2FF;
              border-radius: 50%;
              pointer-events: none;
              transform: scale(0.5) rotate(0deg);
              opacity: 0;
              filter: drop-shadow(0px 0px 4px rgba(255, 255, 255, 0.8));
              animation: bigSparkleBurst 3.5s ease-out infinite;
              clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
            }

            .mobile-menu-button-wrapper::before {
              top: -8px;
              left: -4px;
              background: #F3B2FF;
              animation-delay: 0s;
            }

            .mobile-menu-button-wrapper::after {
              top: -6px;
              right: -6px;
              background: #AEE4FF;
              animation-delay: 0.25s;
            }

            .sparkle-extra {
              position: absolute;
              bottom: -2px;
              right: -8px;
              width: 14px;
              height: 14px;
              background: #B9FFCB;
              border-radius: 50%;
              pointer-events: none;
              transform: scale(0.5) rotate(0deg);
              opacity: 0;
              filter: drop-shadow(0px 0px 4px rgba(255, 255, 255, 0.8));
              animation: bigSparkleBurst 3.5s ease-out infinite;
              animation-delay: 0.5s;
              clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
            }
          }
        `}
      </style>
      <div className="flex h-screen w-full bg-gray-50 overflow-hidden" data-id="element-11290">
        {/* Mobile Hamburger Button - Fixed in TopBar area */}
        <div className="mobile-menu-button-wrapper md:hidden fixed top-3 left-3 z-50" data-id="element-11291">
          <button onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)} className="p-2 bg-white rounded-md shadow-sm border border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-colors" aria-label="Toggle menu" data-id="element-11292">
            {isMobileSidebarOpen ? <XIcon size={20} className="text-gray-700" data-id="element-11293" /> : <MenuIcon size={20} className="mobile-menu-icon" data-id="element-11294" />}
          </button>
          <span className="sparkle-extra" data-id="element-11295"></span>

          {/* Arrow 1: Under Mobile Menu Button - UPWARD */}
          {showAttentionArrows && !isMobileSidebarOpen && <div className={`attention-arrow ${!showAttentionArrows ? 'fade-out' : ''}`} style={{
          position: 'absolute',
          top: '42px',
          left: '50%',
          transform: 'translateX(-50%)',
          pointerEvents: 'none',
          zIndex: 60
        }} data-id="element-11296">
              <svg width="32" height="36" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-11297">
                <defs data-id="element-11298">
                  <linearGradient id="orangeGradient1" x1="0%" y1="100%" x2="0%" y2="0%" data-id="element-11299">
                    <stop offset="0%" stopColor="#FF7A00" data-id="element-11300" />
                    <stop offset="100%" stopColor="#FFA84D" data-id="element-11301" />
                  </linearGradient>
                  <filter id="glow1" data-id="element-11302">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur" data-id="element-11303" />
                    <feMerge data-id="element-11304">
                      <feMergeNode in="coloredBlur" data-id="element-11305" />
                      <feMergeNode in="SourceGraphic" data-id="element-11306" />
                    </feMerge>
                  </filter>
                </defs>
                <path d="M16 36L16 4M16 4L6 14M16 4L26 14" stroke="url(#orangeGradient1)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow1)" data-id="element-11307" />
              </svg>
            </div>}
        </div>

        {/* Mobile Sidebar Overlay */}
        {isMobileSidebarOpen && <div className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-30" onClick={() => setIsMobileSidebarOpen(false)} data-id="element-11308" />}

        {/* Sidebar */}
        <div className={`
            fixed md:relative
            h-full flex-shrink-0 border-r border-gray-200 bg-white
            transition-transform duration-300 ease-in-out
            z-40
            ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            w-64
          `} data-id="element-11309">
          {sidebar}
        </div>

        {/* Main content */}
        <div className="flex flex-col flex-grow overflow-hidden w-full" data-id="element-11310">
          {/* Top bar - Add left padding on mobile to avoid button overlap */}
          <div className="h-auto md:h-16 border-b border-gray-200 bg-white pl-12 md:pl-0 relative" data-id="element-11311">
            {topBar}

            {/* Arrow 2: Under Formulas + Data Model Buttons - UPWARD */}
            {showAttentionArrows && <div className={`attention-arrow ${!showAttentionArrows ? 'fade-out' : ''}`} style={{
            position: 'absolute',
            bottom: '-40px',
            right: '140px',
            pointerEvents: 'none',
            zIndex: 25
          }} data-id="element-11312">
                <svg width="32" height="36" viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-11313">
                  <defs data-id="element-11314">
                    <linearGradient id="orangeGradient2" x1="0%" y1="100%" x2="0%" y2="0%" data-id="element-11315">
                      <stop offset="0%" stopColor="#FF7A00" data-id="element-11316" />
                      <stop offset="100%" stopColor="#FFA84D" data-id="element-11317" />
                    </linearGradient>
                    <filter id="glow2" data-id="element-11318">
                      <feGaussianBlur stdDeviation="2" result="coloredBlur" data-id="element-11319" />
                      <feMerge data-id="element-11320">
                        <feMergeNode in="coloredBlur" data-id="element-11321" />
                        <feMergeNode in="SourceGraphic" data-id="element-11322" />
                      </feMerge>
                    </filter>
                  </defs>
                  <path d="M16 36L16 4M16 4L6 14M16 4L26 14" stroke="url(#orangeGradient2)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow2)" data-id="element-11323" />
                </svg>
              </div>}
          </div>
          {/* Content area */}
          <div ref={contentAreaRef} className="flex-grow overflow-auto p-3 md:p-6 relative" data-id="element-11324">
            {/* Mobile Scroll Indicator */}
            {showScrollIndicator && <div className={`scroll-indicator md:hidden fixed top-20 left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center pointer-events-none ${showScrollIndicator ? '' : 'fade-out'}`} data-id="element-11325">
                <div className="flex flex-col items-center gap-2" style={{
              background: '#1E3A8A',
              color: '#FFFFFF',
              padding: '8px 12px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
            }} data-id="element-11326">
                  <span className="text-sm font-semibold" data-id="element-11327">Scroll down</span>
                  <ChevronDownIcon size={20} className="scroll-indicator-arrow" style={{
                color: '#FFFFFF'
              }} data-id="element-11328" />
                </div>
              </div>}
            <div className="max-w-7xl mx-auto w-full" data-id="element-11329">{children}</div>
          </div>
        </div>
      </div>
    </>;
};
export default DashboardLayout;